import type { ComponentProps, ReactNode } from "react"
import Icon from "@/v2-ui/icon"
import Popover from "@/v2-ui/popover"
import cn from "@/v2-ui/utils/utils.cn"
import Dot from "@/v2-backoffice-client/components/dot"

/**
 * The live duplicate check as one combobox-shaped control:
 *
 *   ┌ input ─────────────────────────────────┐
 *   ├ status row: ● verdict … actions      ⌄ ┤   attached header, always the same height
 *   └────────────────────────────────────────┘
 *   ┌ match list (dropdown, floats) ─────────┐   opens off the header's bottom edge
 *
 * The status row is pinned into the field wrapper's reserved bottom padding,
 * so the form never moves whatever it says; the list is a popover anchored
 * to the whole control (rendered in place like the other pickers in these
 * dialogs), so opening it moves nothing either.
 */

/** Reserved room under the input for the status row (== its height). */
const STATUS_ROW_HEIGHT_CLASS = "h-8"
const STATUS_ROW_PADDING_CLASS = "pb-8"

/**
 * The status row's content slot. Renders nothing while the check is idle —
 * the room is reserved regardless, so the field reads as a plain input until
 * there is a verdict to attach to it.
 */
export type CrmStatusLineProps = ComponentProps<"div">

function CrmStatusLine(props: CrmStatusLineProps) {
  const { className, ...divProps } = props
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "crm-status-row absolute inset-x-0 bottom-0 flex items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-b-md border border-t-0 border-input bg-muted/60 pl-3 pr-1 text-xs",
        STATUS_ROW_HEIGHT_CLASS,
        className,
      )}
      {...divProps}
    />
  )
}

/** An inline text action on the status row ("open", "change"). */
export type CrmStatusActionProps = ComponentProps<"button">

export function CrmStatusAction(props: CrmStatusActionProps) {
  const { className, type = "button", ...buttonProps } = props
  return (
    <button
      type={type}
      className={cn(
        "shrink-0 underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
      {...buttonProps}
    />
  )
}

/** The "checking" pulse — a muted dot breathing, never a spinner. */
export function CrmCheckingDot() {
  return <Dot variant="muted" className="animate-pulse" />
}

/**
 * The chevron at the status row's right edge that opens/closes the match
 * list. Must sit inside a CrmCheckedField, which owns the popover.
 */
export type CrmToggleMatchesButtonProps = {
  open: boolean
}

export function CrmToggleMatchesButton(props: CrmToggleMatchesButtonProps) {
  const { open } = props
  return (
    <Popover.Trigger asChild>
      <button
        type="button"
        aria-label={open ? "Hide matches" : "Show matches"}
        className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Icon src={open ? "angle-small-up" : "angle-small-down"} size={14} />
      </button>
    </Popover.Trigger>
  )
}

/**
 * A form field with a live duplicate check attached (see the diagram at the
 * top). `active` joins the input's bottom corners into the status row
 * (crm.css) once there is a verdict to show.
 */
export type CrmCheckedFieldProps = {
  children: ReactNode
  status: ReactNode
  /** The check is live — the status row is visible and attached. */
  active: boolean
  /** The match list; the popover only mounts when there is one. */
  matches?: ReactNode
  matchesOpen: boolean
  onMatchesOpenChange(open: boolean): void
  className?: string
}

export function CrmCheckedField(props: CrmCheckedFieldProps) {
  const {
    children,
    status,
    active,
    matches,
    matchesOpen,
    onMatchesOpenChange,
    className,
  } = props

  return (
    <Popover
      open={matchesOpen && !!matches}
      onOpenChange={onMatchesOpenChange}
      skipDefaultAnchorOnMobile
    >
      <Popover.Anchor asChild>
        <div
          className={cn(
            "crm-checked-field relative space-y-2",
            STATUS_ROW_PADDING_CLASS,
            className,
          )}
          data-active={active || undefined}
        >
          {children}
          {active && status}
        </div>
      </Popover.Anchor>
      {matches && (
        // Hangs off the status row's bottom edge at the control's width:
        // bg-card lifts it off the dialog, the shadow does the rest, and the
        // list owns its rows edge to edge.
        <Popover.Content
          align="start"
          side="bottom"
          sideOffset={-1}
          size="match-trigger"
          spacing={false}
          skipPortal
          className="overflow-hidden rounded-t-none border-input bg-card p-0 shadow-xl"
        >
          {matches}
        </Popover.Content>
      )}
    </Popover>
  )
}

export default CrmStatusLine
